import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Card, CardProps, Checkbox, Divider, Drawer } from '@mantine/core';
import React, { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { usePairTransactionDrawerController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/PairTransactionDrawer/usePairTransactionDrawerController.ts';
import { DataSecurityTct, GetTransactionRes } from '@dealstack/shared/lib/generated';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { CapTransactionsLoader } from '@dealstack/frontend/shared/components/loaders/CapLoader/CapTransactionsLoader';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { formatTransactionUnits } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/utils.ts';
import { StyledForm } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/styled.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	refreshTransactionsList: (newTransactionId?: string) => void;
	selectedEntityName: string;
	selectedEntityRegNumber?: string;
	transaction: GetTransactionRes;
	securitiesNames?: DataSecurityTct[];
	isFromDetailsPage?: boolean;
}

export const PairTransactionDrawer = ({
	opened,
	onClose,
	refreshTransactionsList,
	transaction,
	selectedEntityName,
	selectedEntityRegNumber,
	securitiesNames,
	isFromDetailsPage = false,
}: Props) => {
	const {
		pairTransactionForm,
		onSubmit,
		personsSelectOptions,
		pairTransactions,
		pairTransactionRequest,
		drawerLoading,
	} = usePairTransactionDrawerController({
		transactionOne: transaction,
		refreshTransactionsList,
		onClose,
		opened,
		isFromDetailsPage,
	});
	const tCapManage = useTranslationCapManage();
	const [expandedTransactions, setExpandedTransactions] = useState<{ [key: string]: boolean }>(
		{}
	);

	const handleSeeMore = (transactionId: string) => {
		setExpandedTransactions((prev) => ({
			...prev,
			[transactionId]: !prev[transactionId],
		}));
	};

	const getSecurityNameById = (securityId: string) => {
		const security = securitiesNames?.find((sec: DataSecurityTct) => sec.id === securityId);
		return security ? security.name : LONG_DASH;
	};

	const getEntityName = (entityId?: string) => {
		if (!entityId) {
			return LONG_DASH;
		}
		const entity = personsSelectOptions.find((option) => option.value === entityId);
		return entity ? entity.label : LONG_DASH;
	};

	const isLoading = pairTransactionRequest.isPending;

	const selectedTransactionId = pairTransactionForm.watch('transactionTwoId');
	const transactionUnits = formatTransactionUnits(transaction);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title="Pair Transaction"
		>
			<FormProvider {...pairTransactionForm}>
				<StyledForm onSubmit={pairTransactionForm.handleSubmit(onSubmit)}>
					<TransactionCard>
						<TransactionTextTitle>
							<NameCircle>
								{selectedEntityName
									.split(' ')
									.map((word) => word[0])
									.slice(0, 2)
									.join('')
									.toUpperCase()}
							</NameCircle>
							{selectedEntityRegNumber ? (
								<NameContent>
									<BoldTitle>{selectedEntityName}</BoldTitle>
									<DescriptionTitle>{selectedEntityRegNumber}</DescriptionTitle>
								</NameContent>
							) : (
								<NameSoloContent>
									<BoldTitle>{selectedEntityName}</BoldTitle>
								</NameSoloContent>
							)}
						</TransactionTextTitle>

						<Divider my="sm" color="#E9EBED" />
						<TransactionTextDescription>
							<NameContent>
								<DetailValue>
									{tCapManage('transactions.drawer.date.label')}
								</DetailValue>
								<BoldTitle>
									{formatBEDateToUiWithFallback(
										transaction?.date,
										DATE_FORMATS.UI,
										LONG_DASH
									)}
								</BoldTitle>
							</NameContent>
							<NameContent>
								<DetailValue>
									{tCapManage('transactions.drawer.security.label')}
								</DetailValue>
								<BoldTitle>
									{getSecurityNameById(transaction?.securityId)}
								</BoldTitle>
							</NameContent>
							<NameContent>
								<DetailValue>
									{tCapManage('transactions.drawer.transaction_type.label')}
								</DetailValue>
								<BoldTitle>{transaction?.type}</BoldTitle>
							</NameContent>
							<NameContent>
								<DetailValue>
									{tCapManage('transactions.drawer.number.label')}
								</DetailValue>
								<BoldTitle>{transactionUnits}</BoldTitle>
							</NameContent>
							<NameContent>
								<DetailValue>
									{tCapManage('transactions.drawer.price_pair.label')}
								</DetailValue>
								<BoldTitle>
									{transaction?.cost?.toLocaleString() || LONG_DASH}
								</BoldTitle>
							</NameContent>
							<NameContent>
								<DetailValue>
									{tCapManage('transactions.drawer.buyer.label')}
								</DetailValue>
								<BoldTitle>{getEntityName(transaction?.destinationId)}</BoldTitle>
							</NameContent>
							<NameContent>
								<DetailValue>
									{tCapManage('transactions.drawer.seller.label')}
								</DetailValue>
								<BoldTitle>{getEntityName(transaction?.sourceId)}</BoldTitle>
							</NameContent>
						</TransactionTextDescription>
					</TransactionCard>

					{drawerLoading ? (
						<CapTransactionsLoader />
					) : (
						<PairDiv>
							{pairTransactions?.length ? (
								<PairCard>
									<BoldTitle>
										{tCapManage(
											'transactions.drawer.potential_transaction.title'
										)}
									</BoldTitle>
									<DetailValue>
										{tCapManage(
											'transactions.drawer.potential_transaction.description'
										)}
									</DetailValue>
								</PairCard>
							) : (
								<NoPairCard>
									<BoldTitle>
										{tCapManage(
											'transactions.drawer.no_potential_transaction.title'
										)}
									</BoldTitle>
									<DetailValue>
										{tCapManage(
											'transactions.drawer.no_potential_transaction.description'
										)}
									</DetailValue>
								</NoPairCard>
							)}

							{pairTransactions &&
								pairTransactions.map((transaction) => (
									<PairTransactionCard key={transaction.id}>
										<PairCheckbox>
											<PairCheckboxBox>
												<Checkbox
													checked={
														pairTransactionForm.watch(
															'transactionTwoId'
														) === transaction.id
													}
													onChange={() =>
														pairTransactionForm.setValue(
															'transactionTwoId',
															transaction.id
														)
													}
												/>
												<BoldTitle>
													{transaction.destinationId
														? getEntityName(transaction.destinationId)
														: getEntityName(transaction.sourceId)}
												</BoldTitle>
											</PairCheckboxBox>

											<PairBadgeDiv>
												<Icon variant="pair-data" size="vlg" /> Data Match
											</PairBadgeDiv>
										</PairCheckbox>
										<PairBox>
											{expandedTransactions[transaction.id] && (
												<PairTransactionMore>
													<NameContent>
														<DetailValue>
															{tCapManage(
																'transactions.drawer.buyer.label'
															)}
														</DetailValue>
														<BoldTitle>
															{getEntityName(
																transaction?.destinationId
															)}
														</BoldTitle>
													</NameContent>
													<NameContent>
														<DetailValue>
															{tCapManage(
																'transactions.drawer.seller.label'
															)}
														</DetailValue>
														<BoldTitle>
															{getEntityName(transaction?.sourceId)}
														</BoldTitle>
													</NameContent>
													<NameContent>
														<DetailValue>
															{tCapManage(
																'transactions.drawer.date.label'
															)}
														</DetailValue>
														<BoldTitle>
															{formatBEDateToUiWithFallback(
																transaction?.date,
																DATE_FORMATS.UI,
																LONG_DASH
															)}
														</BoldTitle>
													</NameContent>
													<NameContent>
														<DetailValue>
															{tCapManage(
																'transactions.drawer.security.label'
															)}
														</DetailValue>
														<BoldTitle>
															{getSecurityNameById(
																transaction?.securityId
															)}
														</BoldTitle>
													</NameContent>
													<NameContent>
														<DetailValue>
															{tCapManage(
																'transactions.drawer.transaction_type.label'
															)}
														</DetailValue>
														<BoldTitle>{transaction?.type}</BoldTitle>
													</NameContent>
													<NameContent>
														<DetailValue>
															{tCapManage(
																'transactions.drawer.number.label'
															)}
														</DetailValue>
														<BoldTitle>
															{formatTransactionUnits(transaction)}
														</BoldTitle>
													</NameContent>
													<NameContent>
														<DetailValue>
															{tCapManage(
																'transactions.drawer.price_pair.label'
															)}
														</DetailValue>
														<BoldTitle>
															{transaction?.cost?.toLocaleString() ||
																LONG_DASH}
														</BoldTitle>
													</NameContent>
												</PairTransactionMore>
											)}
											<Button
												type="button"
												fullWidth
												variant="white"
												onClick={() => handleSeeMore(transaction.id)}
											>
												{expandedTransactions[transaction.id]
													? 'Show less'
													: 'See more'}
											</Button>
										</PairBox>
									</PairTransactionCard>
								))}
						</PairDiv>
					)}

					<Button
						type="submit"
						fullWidth
						mt="auto"
						disabled={!selectedTransactionId || isLoading}
					>
						{tCapManage('transactions.drawer.merge_button')}
					</Button>
					<Button fullWidth variant="default" onClick={onClose}>
						{tCapManage('securities.details.cancel_button')}
					</Button>
				</StyledForm>
			</FormProvider>
		</Drawer>
	);
};

const TransactionCard = styled(Card)<CardProps>`
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	border: none;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
`;

const TransactionTextTitle = styled.div`
	display: flex;
	gap: 16px;
`;

const TransactionTextDescription = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 16px;
`;

const PairTransactionMore = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 16px;
	margin-bottom: 16px;
`;

const NameCircle = styled.div`
	border-radius: 50%;
	background-color: ${({ theme }) => theme.v2.color.neutral[200]};
	width: 48px;
	height: 48px;
	display: flex;
	justify-content: center;
	align-items: center;
	font-weight: 400;
	font-size: 17px;
`;

const NameContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const NameSoloContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const BoldTitle = styled.div`
	font-weight: 600;
	font-size: 14px;
	line-height: 20px;
`;

const DescriptionTitle = styled.div`
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	color: #4f616c;
`;

export const DetailValue = styled.div`
	font-size: 14px;
	line-height: 20px;
	font-weight: 400;
	color: #0e171c;
	display: flex;
	gap: 8px;
	align-items: center;
`;

const PairDiv = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const PairCard = styled(Card)<CardProps>`
	border: 1px solid #b6fbc0;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const NoPairCard = styled(Card)<CardProps>`
	border: 1px solid #fbb6c0;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const PairTransactionCard = styled(Card)<CardProps>`
	border: 1px solid #d3d8da;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 0;
`;

const PairCheckbox = styled.div`
	border-bottom: 1px solid #d3d8da;
	padding: 14px 16px;
	display: flex;
	justify-content: space-between;
`;

const PairCheckboxBox = styled.div`
	display: flex;
	gap: 8px;
	justify-content: space-between;
	align-items: center;
`;

const PairBox = styled.div`
	padding: 14px 16px;
`;

const PairBadgeDiv = styled.div`
	background: #e7feea;
	border: 1px solid #00a36d;
	border-radius: 4px;
	padding: 2px;
	color: #00a36d;
	font-weight: 600;
	font-size: 11px;
	display: flex;
	gap: 8px;
`;
